/**
 * @file The Home page's greeting: "Good morning", "Good afternoon" or "Good
 * evening" by the viewer's local clock (before 12:00, before 18:00, after),
 * the first word of the profile name, and the long date in grey ("Thursday,
 * August 6, 2026"). Consumer: `HomePageContent`, above the tiles. The clock is
 * read at render, so the greeting follows the page's renders, never a timer.
 * A profile with no name yet greets without one. The row wraps, so the date
 * drops below the greeting when the two do not fit side by side.
 */
import { FC, useContext } from 'react';
import { Typography, theme } from 'antd';
import { DateTime } from 'luxon';

import FlexBox from '../../../atoms/FlexBox';
import { UserContext } from '../../../../providers/UserContext';

const { Text, Title } = Typography;

const NOON = 12;
const EVENING = 18;

const greetingOf = (hour: number): string => {
  if (hour < NOON) return 'Good morning';
  if (hour < EVENING) return 'Good afternoon';
  return 'Good evening';
};

export const HomeGreeting: FC = () => {
  const { token } = theme.useToken();
  const { userProfile } = useContext(UserContext);
  const now = DateTime.local().setLocale('en-US');
  const firstName = userProfile?.name?.trim().split(/\s+/)[0] ?? '';
  const greeting = greetingOf(now.hour);
  return (
    <FlexBox noGrow justifyStart wrap gap={10} style={{ alignItems: 'baseline' }}>
      <Title level={3} style={{ margin: 0 }}>
        {firstName === '' ? greeting : `${greeting}, ${firstName}`}
      </Title>
      <Text type="secondary" style={{ fontSize: token.fontSizeLG }}>
        {now.toFormat('cccc, LLLL d, yyyy')}
      </Text>
    </FlexBox>
  );
};
